import React from 'react';
import { useNavigate } from 'react-router-dom';
import CustomModalOverlay from '@/components/common/Custom/CustomModalOverlay/CustomModalOverlay';
import GlobalButton from '@/components/ui/GlobalButton';
import stepImg from '@/assets/images/step1-img.svg';
import { SlackLogo } from '@/svgs';
import useAuthStore from '@/store/Auth';
import { ROUTES } from '@/routes';

interface WelcomeModalProps {
  onContinue: () => void;
  onDismiss: () => void;
}

const welcomeList = [
  {
    title: 'Automate Project Setup',
    description:
      'Just describe your idea and Crafter will generate a structured project, with tickets for your team.',
  },
  {
    title: 'Get Real-Time AI Insights',
    description:
      'Ask Crafter anything about your projects, blockers, performance, deadlines, and more. It learns as your team works.',
  },
  {
    title: 'Collaborate Across Tools',
    description:
      'Sync with Jira, Asana, and other platforms to keep your stack connected and your team aligned.',
  },
];

export default function WelcomeModal({
  onContinue,
  onDismiss,
}: WelcomeModalProps) {
  const navigate = useNavigate();
  const { organization } = useAuthStore();

  const handleIntegrateSlack = () => {
    onDismiss();
    setTimeout(() => {
      navigate(ROUTES.SLACK_INTEGRATION);
    }, 0);
  };

  return (
    <CustomModalOverlay open={true} onClose={onDismiss} width="1100px">
      <div className="w-full flex flex-col items-start text-gray-9">
        <div className="w-full flex items-center justify-between gap-4">
          {/* Image */}
          <div className="hidden lg:block w-full lg:w-[700px] xl:w-[900px]">
            <img
              src={stepImg as unknown as string}
              alt=""
              className="w-full h-full"
            />
          </div>

          {/* Text content */}
          <div className="w-full lg:w-[90%]">
            {/* Header */}
            <div className="border-b-0.5 border-gray-9/30 py-3">
              <h1 className="text-text-primary-heading font-extrabold text-[28px] text-left">
                Welcome to Crafter
              </h1>
              <p className="text-left text-text-primary-body text-sm">
                Crafter helps you turn chaos into clarity by automating project
                creation and surfacing smarter next steps.
              </p>
            </div>

            {/* Body content */}
            <div className="mt-8 space-y-6">
              {welcomeList.map((item, index) => (
                <div key={index} className="text-left">
                  <h2 className="text-text-primary-heading font-bold mb-1 text-lg">
                    {item.title}
                  </h2>
                  <p className="text-text-primary-body text-sm">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-end gap-6 mt-10">
              {!organization?.hasSlackInstalled && (
                <button
                  className="text-white-1 font-bold text-base flex items-center gap-2"
                  onClick={handleIntegrateSlack}
                >
                  <div className="w-[30px]">
                    <SlackLogo />
                  </div>
                  Integrate Slack
                </button>
              )}
              <div>
                <GlobalButton
                  onClick={onContinue}
                  className="w-48 pr-8"
                  size="md"
                  state="default"
                  variant="purple"
                >
                  Get Started
                </GlobalButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </CustomModalOverlay>
  );
}
